import { Fragment, useCallback, useMemo } from 'react';
import { fetchProjects, fetchSprints } from '../../api/endpoints';
import type { Id, Project, Sprint, TicketListItem } from '../../api/types';
import { FilterBar, describeFilters } from '../../components/backlog/FilterBar';
import { TicketDetailPanel } from '../../components/detail/TicketDetailPanel';
import { TicketRow } from '../../components/ticket/TicketRow';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorNotice, Notice, StaleBanner } from '../../components/ui/Notice';
import { ListSkeleton } from '../../components/ui/Skeleton';
import { useResource } from '../../hooks/useResource';
import { useSubtaskIndex } from '../../hooks/useSubtaskIndex';
import {
  buildBacklogRows,
  countNestedOnPage,
  parentIdsToFetch,
  rootIdsOf,
  type SubtaskIndex,
} from '../../lib/ticket-tree';
import { useBacklogParams } from '../../pages/useBacklogParams';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import { SprintSection } from './SprintSection';
import {
  buildSections,
  hideClosedSections,
  sprintSectionTotals,
  unassignedSectionTotals,
  UNASSIGNED_SECTION_KEY,
  UNKNOWN_TOTALS,
  type BacklogSection,
  type SectionTotals,
} from './sprint-sections';
import { ClosedSprintsToggle } from './ClosedSprintsToggle';
import { useClosedSprints } from './useClosedSprints';
import { applyOrder, isReorderable, reorderableIds } from './rank-drop';
import { CompleteSprintDialog } from './CompleteSprintDialog';
import { SprintFormDialog, type VersionChoices } from './SprintFormDialog';
import { carryOverPreview } from './sprint-close';
import { todayCalendarDate } from './sprint-form';
import { useCompleteSprint } from './useCompleteSprint';
import { useRankReorder, type ReorderState } from './useRankReorder';
import { useSectionCollapse } from './useSectionCollapse';
import { useSprintEditor } from './useSprintEditor';
import { TicketFormDialog } from '../tickets/TicketFormDialog';
import { useTicketEditor } from '../tickets/useTicketEditor';
import { useStartSprint } from './useStartSprint';
import { SECTION_PAGE_SIZE, useSprintSectionIndex, useUnassignedScan } from './useSprintBacklogData';
import { fetchVersions, type VersionsResult } from '../versions/versions-api';

/**
 * 🔴 THE BACKLOG, GROUPED BY SPRINT — Or's request: *"Backlog with separate sprints and last one will
 * be unassign sprint grouping with collapse of each section"*.
 *
 * ⭐ THIS SCREEN IS FOR ORDERING AND PLANNING. Its counterpart, the List view, is for FINDING. That
 * distinction decides everything about both: this one groups, sums points, nests sub-tasks under
 * their parents and keeps each sprint in `backlog_rank` order; the List one is a flat, dense,
 * server-sorted, server-paged table. ⛔ They are deliberately not the same screen twice.
 *
 * ⚠️ IT DOES NOT RENDER THE TOP BAR. The shell owns `App.tsx` and `components/layout`, and a page
 * that draws its own chrome inside a shell that also draws it gives you two of them.
 */

const NO_SPRINTS: Sprint[] = [];
const NO_ITEMS: TicketListItem[] = [];

export function SprintBacklogPage() {
  const vocab = useVocabulary();
  const { filters, selectedId, expanded, setFilters, select, toggleExpanded } = useBacklogParams();

  const projects = useResource<Project[]>((signal) => fetchProjects(signal), []);
  const projectId = filters.projectId;

  const sprintsResource = useResource<Sprint[]>(
    (signal) => fetchSprints(projectId as Id, signal),
    [projectId],
    { enabled: Boolean(projectId) },
  );
  const sprints = sprintsResource.data ?? NO_SPRINTS;

  /**
   * 🔴 PH-52 — CLOSED SPRINTS ARE HIDDEN FROM THE DRAW, AND THIS IS THE ONLY PLACE IT HAPPENS.
   *
   * ⛔⛔ NOTE WHAT IS **NOT** FILTERED: `sprints` above. That array is what
   *   `CompleteSprintDialog` is handed for its carry-over picker, what `FilterBar` lists in its
   *   sprint dropdown, and what `defaultOpenKeys` reads. Narrowing it would break a picker whose
   *   entire job is to offer the sprints the API accepts — and the API's rule (`carryOverTargets`)
   *   is about the CLOSING sprint and closed TARGETS, which is a different question from what this
   *   screen draws. ⇒ Filter at render; never at fetch.
   * ⚠️ The request is unchanged too, closed sprints included: `ticket_count`,
   *   `story_points_total` and PH-37's progress fields come back in one server-side aggregate, so a
   *   revealed section already has its numbers and costs no extra round trip.
   */
  const closed = useClosedSprints();

  /**
   * ⭐ A sprint FILTER inside a sprint-GROUPED view is not a contradiction — it narrows the page to
   * one section. ⛔ But the unassigned section then disappears, because "no sprint" and "sprint 12"
   * cannot both be true, and an empty Backlog section under it would read as "there are no unplanned
   * tickets" rather than "you are filtered".
   */
  const allSections = useMemo<BacklogSection[]>(() => {
    const all = buildSections(sprints);
    if (!filters.sprintId) return all;
    return all.filter((section) => section.key === filters.sprintId);
  }, [sprints, filters.sprintId]);

  /**
   * ⭐ `keep: filters.sprintId` — a sprint named by the filter is never hidden, even when it is
   *   closed. The picker offers closed sprints on purpose (reviewing one is why you would filter to
   *   it), and answering that with an empty page reads as "this sprint is empty", not "you are
   *   filtered". An explicit request beats a default.
   */
  const closedView = useMemo(
    () => hideClosedSections(allSections, { show: closed.show, keep: filters.sprintId }),
    [allSections, closed.show, filters.sprintId],
  );
  const sections = closedView.sections;

  const collapse = useSectionCollapse(sprints);

  /**
   * ⭐⭐ ONLY THE OPEN SECTIONS ASK FOR THEIR ROWS. A collapsed header is already answerable from
   * `GET /projects/:id/sprints` (`ticket_count`, `story_points_total`), so a project with fourteen
   * sprints costs ONE ticket request on load rather than fourteen.
   */
  const openSprintIds = useMemo(
    () =>
      sections
        .filter((section) => section.sprint !== null && collapse.isOpen(section.key))
        .map((section) => section.key),
    [sections, collapse],
  );

  const sprintPages = useSprintSectionIndex(projectId, openSprintIds, filters);

  // ✅ One request asking `noSprint=true`, not a scan over the project's pages. The page counter and
  //    its reset effect went with the workaround — see `useUnassignedScan`.
  const unassigned = useUnassignedScan(projectId, filters);

  const itemsOf = useCallback(
    (section: BacklogSection): TicketListItem[] => {
      if (section.sprint) return sprintPages.data?.index.get(section.key)?.items ?? NO_ITEMS;
      return unassigned.data?.items ?? NO_ITEMS;
    },
    [sprintPages.data, unassigned.data],
  );

  const filtered = Boolean(filters.status || filters.assignee);

  const totalsOf = useCallback(
    (section: BacklogSection): SectionTotals => {
      if (section.sprint) {
        if (!sprintsResource.data) return UNKNOWN_TOTALS;
        return sprintSectionTotals(
          section.sprint,
          sprintPages.data?.index.get(section.key) ?? null,
          filtered,
        );
      }
      if (!unassigned.data) return UNKNOWN_TOTALS;
      return unassignedSectionTotals(
        unassigned.data.items,
        unassigned.data.total,
        unassigned.data.truncated,
      );
    },
    [sprintsResource.data, sprintPages.data, unassigned.data, filtered],
  );

  /**
   * 🔴 SUB-TASK NESTING IS PRESERVED, PER SECTION, BY THE RULES IN `ticket-tree.ts`. A child sits with
   * its parent and NEVER at its own `updated_at` position — the inversion those rules exist for. The
   * only thing this page changes is the SCOPE: "this page" becomes "this section", so a sub-task
   * whose parent is in a DIFFERENT sprint is `detached` and says so, exactly as one whose parent is
   * on another page does.
   *
   * ⛔ AND THAT IS WHY THIS SCREEN DOES NOT USE THE API'S NEW `rootOnly`. `rootOnly=true` would drop
   * that sub-task from its own sprint's section and draw it under a parent sitting in a different
   * sprint — a row silently moved into a sprint it is not in. Shown out of place and labelled beats
   * moved without saying so.
   *
   * ⚠️ THE ONE CASE WHERE A TICKET IS ON SCREEN TWICE, AND WHY THAT IS THE CHOSEN SIDE OF THE TRADE.
   * A sub-task carried into the NEXT sprint while its parent stays in this one appears (a) nested
   * under its parent, because it is its parent's child, and (b) in its own sprint's section badged
   * `↳ sub-task`, because that is the sprint it is in. Both statements are true, both are labelled,
   * and the row carries its own sprint chip, so the pair reads as "one child slipped to v1.0.34" —
   * which is the thing a planner needs to see. ⛔ The alternative is to nest only children that are
   * in this section's own page, and that DROPS a same-sprint child sitting past row 100 — invisible,
   * and the one outcome this codebase does not allow. A visible duplicate beats a silent omission.
   *
   * ⭐ ONE index for the whole screen: parents are collected across every section first, so a parent
   * appearing in two sections costs one request, not two.
   */
  const parentIds = useMemo(() => {
    const wanted = new Set<Id>();
    for (const section of sections) {
      const items = itemsOf(section);
      for (const id of parentIdsToFetch(items)) wanted.add(id);
      const roots = rootIdsOf(items);
      for (const id of expanded) if (roots.has(id)) wanted.add(id);
    }
    return [...wanted];
  }, [sections, itemsOf, expanded]);

  const subtasks = useSubtaskIndex(parentIds);

  const assigneeSuggestions = useMemo(() => {
    const names = new Set<string>();
    for (const section of sections) {
      for (const item of itemsOf(section)) if (item.assignee) names.add(item.assignee);
    }
    return [...names].sort();
  }, [sections, itemsOf]);

  const onStatusChanged = useCallback(() => {
    // Every copy. A nested child's status is served by the sub-task index, not by the section page,
    // so refreshing only one leaves the other showing a status that has already changed.
    sprintPages.reload();
    unassigned.reload();
    subtasks.reload();
    // ⭐ And the sprint list too: its `ticket_count`/`story_points_total` are what a COLLAPSED header
    //   shows, and a transition can move a ticket out of the set they counted.
    sprintsResource.reload();
  }, [sprintPages, unassigned, subtasks, sprintsResource]);

  /**
   * ⭐ A REORDER REFETCHES THE SECTION PAGES, not just the moved row. The server assigns the actual
   *   sparse `backlog_rank`, and that value is what the NEXT drag computes its neighbours from — so a
   *   list left holding the optimistic arrangement would compute the second move against an order the
   *   database never had.
   * ⛔ The sprint list is deliberately NOT reloaded: a rank change moves nothing between sprints, so
   *   `ticket_count` and `story_points_total` are untouched. Reloading it would be a request that
   *   cannot change its own answer.
   */
  const reorder = useRankReorder(
    useCallback(() => {
      sprintPages.reload();
      unassigned.reload();
    }, [sprintPages, unassigned]),
  );

  /**
   * 🔴 COMPLETING A SPRINT MOVES TICKETS BETWEEN SECTIONS, so EVERYTHING on this screen is refetched
   *   — and that is a stronger requirement than the reorder's. The sprint's own state becomes
   *   `closed` (the header badge and the action), its rows leave it, and they arrive in the Backlog
   *   section or in another sprint. ⛔ Reloading only the closed section would leave the destination
   *   showing a list the database no longer has, which is the "stale looks current" failure this
   *   codebase refuses.
   */
  const complete = useCompleteSprint(
    useCallback(() => {
      sprintsResource.reload();
      sprintPages.reload();
      unassigned.reload();
      subtasks.reload();
    }, [sprintsResource, sprintPages, unassigned, subtasks]),
  );

  /**
   * What we may honestly tell the user is about to move.
   *
   * ⚠️ Deliberately derived from THE SECTION THE PAGE ALREADY HOLDS rather than from a fresh
   *   request: the rows are on screen, and asking again would still be capped at 100 and still
   *   filtered. ⭐ What matters is that the SCOPE travels with the number — `carryOverPreview`
   *   returns `unknown` for a collapsed section instead of `0`.
   */
  const askingPreview = useMemo(
    () =>
      carryOverPreview(
        complete.asking ? (sprintPages.data?.index.get(complete.asking.id) ?? null) : null,
        filtered,
        vocab.statuses,
      ),
    [complete.asking, sprintPages.data, filtered, vocab.statuses],
  );

  /**
   * 🔴 PH-23 — CREATE · START · EDIT. The API has had all three since `routes/sprints.ts` was
   *   written; this screen had a caller for none of them, so the only sprint write the app could make
   *   was the last one in the lifecycle. Or found that on his phone: he came to the backlog to make a
   *   sprint the way he would in Jira and there was no such control at any width.
   *
   * ⭐⭐ ONLY THE SPRINT LIST IS RELOADED, AND THAT IS THE EXACT MIRROR OF THE REORDER RULE ABOVE.
   *   None of these three writes moves a TICKET: create makes an empty sprint, start flips one
   *   column, edit changes a name, a goal, two dates or a release link. So the section pages and the
   *   sub-task index cannot have changed, and refetching them would be requests that cannot change
   *   their own answers. ⛔ Contrast `complete`, which refetches everything precisely because it does
   *   move tickets between sections.
   * ⚠️ The sprint list reload is NOT optional though: `state`, `name` and the dates are all rendered
   *   on the header, and `defaultOpenKeys` picks which section opens from `state`. A page left
   *   holding the old list would show a started sprint still badged PLANNED and still offering Start
   *   — which would then 409. "Stale looks current", the failure this codebase refuses.
   * ⭐ And a NEW section may now be open, which changes `openSprintIds` — `useSprintSectionIndex`
   *   fetches its rows off the back of that dependency, so nothing has to ask for them here.
   */
  const reloadSprintList = useCallback(() => {
    sprintsResource.reload();
  }, [sprintsResource]);

  const editor = useSprintEditor(projectId, reloadSprintList);
  const starter = useStartSprint(reloadSprintList);

  /**
   * PH-57's editor. ⭐ It reloads the SAME list the sprint editor does — a new ticket lands in a
   * section, and a ticket whose sprint changed moves between two of them, so the screen has to be
   * re-read either way rather than patched in place.
   */
  const ticketEditor = useTicketEditor(projectId, reloadSprintList);

  /**
   * The release picker's options — fetched ONLY while the form is open.
   *
   * ⭐ `enabled` rather than an unconditional load: every reader of this screen pays for the sprint
   *   list and its sections, and almost none of them open the sprint form. ⛔ And it is the shared
   *   `fetchVersions`, which already tells `no such route` (the endpoint is not deployed) apart from
   *   `project not found` (the id is wrong) — a distinction this dialog surfaces rather than
   *   flattening into an empty picker.
   */
  const versionsResource = useResource<VersionsResult>(
    (signal) => fetchVersions(projectId as Id, signal),
    [projectId],
    // ⭐ Now EITHER form needs the release picker, so the condition widens rather than a second
    //   fetch being added — one request serves whichever dialog is open, and neither pays for it
    //   while both are closed.
    { enabled: Boolean(projectId) && (editor.open !== null || ticketEditor.open !== null) },
  );

  const versionChoices = useMemo<VersionChoices>(() => {
    if (versionsResource.error) {
      return {
        kind: 'unavailable',
        reason: versionsResource.error.reason ?? versionsResource.error.message,
      };
    }
    if (!versionsResource.data) return { kind: 'loading' };
    if (versionsResource.data.kind === 'endpoint-missing') {
      return { kind: 'unavailable', reason: `${versionsResource.data.path} is not deployed` };
    }
    return { kind: 'ok', versions: versionsResource.data.versions };
  }, [versionsResource.data, versionsResource.error]);

  const statusLabel = filters.status ? (vocab.status(filters.status)?.label ?? filters.status) : null;
  const filterSummary = describeFilters(filters, projects.data ?? [], statusLabel);
  const sectionKeys = sections.map((section) => section.key);
  const listClass = selectedId
    ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_26rem] xl:grid-cols-[minmax(0,1fr)_30rem]'
    : '';

  const sprintFailures = sprintPages.data?.failed ?? [];
  const subtaskFailures = subtasks.data?.failed ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={`min-h-0 flex-1 ${listClass}`}>
        <section
          aria-label="Backlog by sprint"
          className="flex h-full min-h-0 flex-col bg-[color:var(--color-surface)]"
        >
          <FilterBar
            projects={projects.data ?? []}
            statuses={vocab.statuses}
            assigneeSuggestions={assigneeSuggestions}
            sprints={sprints}
            sprintsUnavailable={Boolean(sprintsResource.error)}
            filters={filters}
            onChange={setFilters}
          />

          <div className="space-y-2 px-3 pt-2 empty:hidden sm:px-4">
            {projects.error ? <ErrorNotice error={projects.error} onRetry={projects.reload} /> : null}

            {sprintsResource.error && !sprintsResource.isStale ? (
              <ErrorNotice error={sprintsResource.error} onRetry={sprintsResource.reload} />
            ) : null}

            {/* 🔴 A REFUSED OR FAILED REORDER SAYS SO. The list has already snapped back by the time
                this renders, and a list that silently returns to its old order is exactly the "it
                did nothing" failure this codebase refuses — the row moved back, so something must
                explain why. ⭐ Includes the API's own 409 text ("rank it first"), which is the
                actionable half. */}
            {reorder.error ? (
              <Notice
                tone="warning"
                title="That reorder was not saved"
                action={
                  <button
                    type="button"
                    onClick={reorder.dismissError}
                    className="h-8 rounded border border-[color:var(--color-line)] px-2.5 text-sm font-medium"
                  >
                    Dismiss
                  </button>
                }
              >
                <p>{reorder.error}</p>
              </Notice>
            ) : null}

            {/* 🔴🔴 PH-23's 409, VERBATIM AND ABOVE THE FOLD.
                `POST /sprints/:id/start` refuses a second active sprint with
                `this project already has an active sprint ('APFS v1.0.33'); close it before starting
                another` — the API grew a pre-check purely so that NAME would exist.
                ⛔ "Could not start sprint" is the failure mode this replaces: the blocking sprint can
                be off screen or in a collapsed section, so its name is the only thing that tells the
                reader what to go and close.
                ⭐ THIS NOTICE SITS OUTSIDE THE SCROLLING LIST, which is what makes it work at 390px:
                the Start button may have been ten sections down, and a message rendered beside it
                would be a message the user has already scrolled past. */}
            {starter.error ? (
              <Notice
                tone="error"
                title="That sprint was not started"
                action={
                  <button
                    type="button"
                    onClick={starter.dismissError}
                    className="h-8 rounded border border-[color:var(--color-line)] px-2.5 text-sm font-medium"
                  >
                    Dismiss
                  </button>
                }
              >
                <p className="break-words">{starter.error}</p>
              </Notice>
            ) : null}

            {starter.outcome ? (
              <Notice
                tone="info"
                title="Sprint started"
                action={
                  <button
                    type="button"
                    onClick={starter.dismissOutcome}
                    className="h-8 rounded border border-[color:var(--color-line)] px-2.5 text-sm font-medium"
                  >
                    Dismiss
                  </button>
                }
              >
                <p>{starter.outcome}</p>
              </Notice>
            ) : null}

            {/* ⭐ A created sprint appears COLLAPSED and far down a long list — the same "did that
                work?" problem the completion notice solves, arriving from the other direction. */}
            {editor.outcome ? (
              <Notice
                tone="info"
                title="Sprint saved"
                action={
                  <button
                    type="button"
                    onClick={editor.dismissOutcome}
                    className="h-8 rounded border border-[color:var(--color-line)] px-2.5 text-sm font-medium"
                  >
                    Dismiss
                  </button>
                }
              >
                <p>{editor.outcome}</p>
              </Notice>
            ) : null}

            {/* ⭐ THE KEY IS THE PAYLOAD. A new ticket is appended to the BOTTOM of its section's
                rank order (`createTicket` gives it the next rank), so on a 58-ticket backlog it is
                created off-screen. The notice carries `PH-59 — title` because that key is the only
                handle a person has for finding it again. */}
            {ticketEditor.outcome ? (
              <Notice
                tone="info"
                title="Ticket saved"
                action={
                  <button
                    type="button"
                    onClick={ticketEditor.dismissOutcome}
                    className="h-8 rounded border border-[color:var(--color-line)] px-2.5 text-sm font-medium"
                  >
                    Dismiss
                  </button>
                }
              >
                <p>{ticketEditor.outcome}</p>
              </Notice>
            ) : null}

            {/* ⭐ WHAT THE SERVER SAID IT DID, not what we predicted it would do. The sprint has
                vanished from the top of the page (it is closed now and its rows have moved), and an
                irreversible action that leaves no trace on screen reads as "did that work?". */}
            {complete.outcome ? (
              <Notice
                tone="info"
                title="Sprint completed"
                action={
                  <button
                    type="button"
                    onClick={complete.dismissOutcome}
                    className="h-8 rounded border border-[color:var(--color-line)] px-2.5 text-sm font-medium"
                  >
                    Dismiss
                  </button>
                }
              >
                <p>{complete.outcome}</p>
              </Notice>
            ) : null}

            {sprintPages.isStale && sprintPages.error ? (
              <StaleBanner
                error={sprintPages.error}
                loadedAt={sprintPages.loadedAt}
                onRetry={sprintPages.reload}
              />
            ) : null}

            {/* ⛔ Reported at page level, not only inside the section — a section that failed and was
                then collapsed would otherwise say nothing at all. */}
            {sprintFailures.length > 0 ? (
              <Notice
                tone="warning"
                title={`${sprintFailures.length} sprint section${
                  sprintFailures.length === 1 ? '' : 's'
                } could not be loaded`}
                action={
                  <button
                    type="button"
                    onClick={sprintPages.reload}
                    className="h-8 rounded border border-[color:var(--color-line)] px-2.5 text-sm font-medium"
                  >
                    Retry
                  </button>
                }
              >
                <p>
                  {sprintFailures[0]?.reason} — those sections show no rows. They are not empty; we
                  could not ask.
                </p>
              </Notice>
            ) : null}

            {subtaskFailures.length > 0 ? (
              <Notice
                tone="warning"
                title={`Sub-tasks could not be loaded for ${subtaskFailures.length} ticket${
                  subtaskFailures.length === 1 ? '' : 's'
                }`}
                action={
                  <button
                    type="button"
                    onClick={subtasks.reload}
                    className="h-8 rounded border border-[color:var(--color-line)] px-2.5 text-sm font-medium"
                  >
                    Retry
                  </button>
                }
              >
                <p>
                  {subtaskFailures[0]?.reason} — those rows show only the sub-tasks that happen to be
                  in the same section, and their counts are marked <code>+</code>.
                </p>
              </Notice>
            ) : null}
          </div>

          {!projectId ? (
            <EmptyState
              title="Choose a project to see its sprints"
              detail="Sprint names are unique only within a project (db/001), so two projects can each have a v1.0.33. There is no all-projects sprint list to group by — pick one above."
            />
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto">
              {sprintsResource.isLoading ? <ListSkeleton /> : null}

              {sprintsResource.data ? (
                <>
                  <div className="flex flex-wrap items-center gap-1.5 px-3 py-1.5 sm:px-4">
                    {/* 🔴 THE CONTROL PH-23 IS ABOUT, AND IT IS FIRST IN THE ROW.
                        ⭐ Placed at the TOP of the sprint list rather than at the bottom or behind a
                        menu: a project with fourteen sprints scrolls, and "make a new sprint" is not
                        a thing you should have to reach the end of the backlog to find — least of
                        all at 390px, which is where it was reported missing.
                        ⛔ It is NOT admin-gated. The API gates every sprint write on ACTIVE PROJECT
                        MEMBERSHIP (`requireUser` + `assertMember`), not on `is_admin`, and its own
                        comment says why: an admin-only sprint board would mean a team cannot start
                        its own sprint. A UI check stricter than the server's is a feature removed
                        from the people entitled to it. */}
                    {/* 🔴 THE CONTROL PH-57 IS ABOUT, and it is FIRST — ahead of "New sprint",
                        because making a ticket is the more common act by a wide margin and this is
                        the screen a person lands on. Until this button existed every ticket in
                        PlanHub had been created by an agent over the API or the MCP.
                        ⛔ Not admin-gated, for the same reason the sprint button is not: the API
                        gates ticket writes on ACTIVE PROJECT MEMBERSHIP, and a UI check stricter
                        than the server's removes a feature from the people entitled to it. */}
                    <button
                      type="button"
                      onClick={() => ticketEditor.create()}
                      className="h-8 rounded bg-[color:var(--color-accent)] px-2.5 text-[13px] font-semibold text-[color:var(--color-on-accent)]"
                    >
                      New ticket
                    </button>

                    <button
                      type="button"
                      onClick={editor.create}
                      className="h-8 rounded border border-[color:var(--color-line)] px-2.5 text-[13px] font-semibold"
                    >
                      New sprint
                    </button>

                    {/* `ml-auto` so the two bulk toggles stay together on the right and wrap as a
                        pair — at 390px all three fit on one line; below that they wrap cleanly. */}
                    <button
                      type="button"
                      onClick={() => collapse.setAll(sectionKeys, true)}
                      className="ml-auto h-8 rounded border border-[color:var(--color-line)] px-2.5 text-[13px] font-medium"
                    >
                      Expand all
                    </button>
                    <button
                      type="button"
                      onClick={() => collapse.setAll(sectionKeys, false)}
                      className="h-8 rounded border border-[color:var(--color-line)] px-2.5 text-[13px] font-medium"
                    >
                      Collapse all
                    </button>
                  </div>

                  {sections.map((section, index) => {
                    const isSprint = section.sprint !== null;
                    const loaded = isSprint
                      ? Boolean(sprintPages.data?.index.get(section.key))
                      : Boolean(unassigned.data);
                    const failure = sprintFailures.find((entry) => entry.sprintId === section.key);

                    /**
                     * 🔴 PH-52's control, AT THE BOUNDARY — the index the closed block starts at, so
                     *   it sits exactly where the missing sections were and the revealed ones appear
                     *   directly below it.
                     * ⭐ `hideable > 0` is the whole render condition: a project with no closed
                     *   sprint draws nothing, and neither does the one case where the toggle could
                     *   not move anything (a closed sprint pinned by the sprint filter).
                     * ⚠️ There is no "boundary past the last section" case to handle: the closed
                     *   sprints are followed by the unassigned `Backlog` section whenever the sprint
                     *   filter is off, and when it is ON the single section it leaves is its own
                     *   `keep`, which makes `hideable` zero.
                     */
                    const boundary =
                      closedView.hideable > 0 && index === closedView.boundary ? (
                        <ClosedSprintsToggle
                          count={closedView.hideable}
                          show={closed.show}
                          onToggle={closed.setShow}
                        />
                      ) : null;

                    return (
                      <Fragment key={section.key}>
                        {boundary}
                        <SectionBody
                          section={section}
                          items={itemsOf(section)}
                          totals={totalsOf(section)}
                          open={collapse.isOpen(section.key)}
                          onToggle={() => collapse.toggle(section.key)}
                          busy={isSprint ? sprintPages.isRefreshing : unassigned.isRefreshing}
                          loading={
                            !loaded &&
                            !failure &&
                            (isSprint
                              ? sprintPages.isLoading || sprintPages.isRefreshing
                              : unassigned.isLoading)
                          }
                          error={
                            failure?.reason ?? (isSprint ? null : (unassigned.error?.reason ?? null))
                          }
                          expanded={expanded}
                          subtasks={subtasks.data?.index}
                          selectedId={selectedId}
                          onSelect={select}
                          onToggleExpanded={toggleExpanded}
                          onStatusChanged={onStatusChanged}
                          filtered={filtered}
                          filterSummary={filterSummary}
                          reorder={reorder}
                          /**
                           * 🔴 THE STATE GUARD, AND IT IS THE API'S RULE — NOT A PREFERENCE.
                           *   `closeSprint` refuses anything that is not `active` with a 409, so an
                           *   action on a planned or closed sprint would be a button whose only
                           *   possible outcome is a refusal. ⛔ And it is `=== 'active'`, not
                           *   `!== 'closed'`: a planned sprint has to be STARTED first.
                           * ⚠️ `section.sprint` is null for the unassigned Backlog pile, which is not a
                           *   sprint and can never be completed.
                           */
                          onComplete={
                            section.sprint?.state === 'active'
                              ? () => complete.ask(section.sprint as Sprint)
                              : undefined
                          }
                          completing={complete.busy && complete.asking?.id === section.key}
                          /**
                           * 🔴 THE STATE GUARD AGAIN, AND AGAIN IT IS THE API'S RULE. `startSprint`
                           *   refuses anything that is not `planned` with a 409 — `this sprint is
                           *   already active`, or `a 'closed' sprint cannot be started; only a planned
                           *   sprint can`. So the action exists on `planned` and nowhere else.
                           * ⛔ It is NOT hidden when another sprint is already active. That refusal is
                           *   a RACE the client cannot see honestly: the blocking sprint may have been
                           *   started by somebody else since this list loaded, and a button hidden on
                           *   stale data is a feature that vanishes for no stated reason. ⇒ The button
                           *   stays and the API's 409 names the blocker — which is the sentence the
                           *   API grew a pre-check specifically to be able to say.
                           */
                          onStart={
                            section.sprint?.state === 'planned'
                              ? () => starter.start(section.sprint as Sprint)
                              : undefined
                          }
                          starting={starter.busyId === section.key}
                          /**
                           * ⚠️ PLANNED AND ACTIVE ONLY — AND THIS ONE IS A UI CHOICE, NOT AN API RULE.
                           *   Said plainly because the two are different kinds of claim and this file
                           *   is careful about the difference elsewhere: `PATCH /sprints/:id` has NO
                           *   state guard at all and would happily rename a closed sprint. What stops
                           *   it here is that a closed sprint's name and dates are the caption on a
                           *   retro snapshot — `committed_pts`/`completed_pts` were true AT CLOSE and
                           *   never again — so editing them re-labels a record of something that
                           *   already happened. If somebody needs that, it is a deliberate feature and
                           *   not an accident of leaving the button on.
                           */
                          onEdit={
                            section.sprint && section.sprint.state !== 'closed'
                              ? () => editor.edit(section.sprint as Sprint)
                              : undefined
                          }
                          scan={
                            isSprint || !unassigned.data
                              ? null
                              : {
                                  total: unassigned.data.total,
                                  shown: unassigned.data.items.length,
                                  truncated: unassigned.data.truncated,
                                }
                          }
                        />
                      </Fragment>
                    );
                  })}
                </>
              ) : null}
            </div>
          )}
        </section>

        {selectedId ? (
          <div className="fixed inset-0 z-40 lg:static lg:z-auto lg:h-full lg:min-h-0">
            <TicketDetailPanel
              ticketId={selectedId}
              onClose={() => select(null)}
              onOpenTicket={(id) => select(id)}
              onStatusChanged={onStatusChanged}
            />
          </div>
        ) : null}
      </div>

      {/* ⭐ `sprints`, not `sections` — the destination list is a question about the PROJECT's
          sprints, and `sections` may have been narrowed to one by the sprint filter. Offering only
          the filtered sprint as a destination would hide every legal target. */}
      {complete.asking ? (
        <CompleteSprintDialog
          sprint={complete.asking}
          sprints={sprints}
          preview={askingPreview}
          busy={complete.busy}
          error={complete.error}
          onCancel={complete.cancel}
          onConfirm={complete.confirm}
        />
      ) : null}

      {/* ⭐ `today` is computed HERE, at render, rather than inside the dialog — one place asks what
          day it is, and a test can pin it. See `todayCalendarDate`: the local getters are deliberate
          and `toISOString().slice(0,10)` would default every sprint created before 07:00 in Bangkok
          to yesterday. */}
      {editor.open ? (
        <SprintFormDialog
          mode={editor.open}
          today={todayCalendarDate()}
          versions={versionChoices}
          busy={editor.busy}
          error={editor.error}
          problem={editor.problem}
          onCancel={editor.cancel}
          onSubmit={editor.submit}
        />
      ) : null}

      {/* ⛔ The release picker is passed as a plain ARRAY here, not as `VersionChoices`. The sprint
          form must distinguish "this project has no releases" from "we could not ask" because a
          sprint is often created FOR a release; a ticket's release is an incidental optional field,
          and an empty picker on it is not a claim worth three states. If that stops being true the
          fix is to pass `versionChoices` through, not to invent a second shape. */}
      {ticketEditor.open ? (
        <TicketFormDialog
          mode={ticketEditor.open}
          sprints={sprints}
          versions={versionChoices.kind === 'ok' ? versionChoices.versions : []}
          busy={ticketEditor.busy}
          error={ticketEditor.error}
          problem={ticketEditor.problem}
          onCancel={ticketEditor.cancel}
          onSubmit={ticketEditor.submit}
        />
      ) : null}
    </div>
  );
}

/**
 * What the unassigned section knows about its own completeness.
 *
 * ⚠️ NOT the old scan state. There is no page counter and no Scan-more button any more, because the
 *   section is one server-answered request. What remains is the page cap: `shown < total` means the
 *   API returned its first 100 of a larger set, and the footer says so rather than letting the list
 *   imply it is everything.
 */
interface ScanState {
  total: number;
  shown: number;
  truncated: boolean;
}

function SectionBody({
  section,
  items,
  totals,
  open,
  onToggle,
  busy,
  loading,
  error,
  expanded,
  subtasks,
  selectedId,
  onSelect,
  onToggleExpanded,
  onStatusChanged,
  filtered,
  filterSummary,
  scan,
  reorder,
  onComplete,
  completing,
  onStart,
  starting,
  onEdit,
}: {
  section: BacklogSection;
  items: TicketListItem[];
  totals: SectionTotals;
  open: boolean;
  onToggle: () => void;
  busy: boolean;
  loading: boolean;
  error: string | null;
  expanded: ReadonlySet<Id>;
  subtasks: SubtaskIndex | undefined;
  selectedId: Id | null;
  onSelect: (id: Id | null) => void;
  onToggleExpanded: (id: Id) => void;
  onStatusChanged: () => void;
  filtered: boolean;
  filterSummary: string;
  scan: ScanState | null;
  reorder: ReorderState;
  /** ⭐ ABSENT means the sprint is not in a completable state — see the guard at the call site. */
  onComplete?: () => void;
  completing: boolean;
  /** ⭐ ABSENT means the sprint is not `planned`, so the API would refuse a start. */
  onStart?: () => void;
  starting: boolean;
  /** ⭐ ABSENT for a closed sprint and for the unassigned pile — see the call site. */
  onEdit?: () => void;
}) {
  // ⭐ TWO PASSES, DELIBERATELY. The optimistic order is expressed in TOP-LEVEL ids, and which rows
  //   are top-level is a question only the tree builder answers — so the rows are built once to find
  //   out, reshuffled, and built again. The second pass is skipped entirely when nothing is in
  //   flight, which is every render but the handful during a drag.
  const baseRows = useMemo(
    () => buildBacklogRows(items, { subtasks, expanded }),
    [items, subtasks, expanded],
  );
  const pendingOrder = reorder.orderFor(section.key, baseRows);
  const rows = useMemo(
    () =>
      pendingOrder
        ? buildBacklogRows(applyOrder(items, pendingOrder), { subtasks, expanded })
        : baseRows,
    [pendingOrder, items, subtasks, expanded, baseRows],
  );
  const nested = useMemo(() => countNestedOnPage(items), [items]);

  // The ids a drop may land on, in the order they are drawn — recomputed from the rows ACTUALLY
  // rendered, so an optimistic move is what the next drag measures against.
  const order = useMemo(() => reorderableIds(rows), [rows]);

  return (
    <SprintSection
      section={section}
      open={open}
      onToggle={onToggle}
      totals={totals}
      filtered={filtered}
      busy={busy}
      action={
        onComplete || onStart || onEdit ? (
          <>
            {/**
             * ⚠️ EVERY LABEL IS THE SAME AT EVERY WIDTH — the rule that predates PH-23 and still
             * holds. A shortened label on the phone makes the accessible name differ between
             * viewports, which is one control under two names. They are small type in compact
             * buttons instead, and the sprint NAME truncates rather than any of these.
             *
             * 🔴 390px BUDGET, MEASURED IN CHARACTERS RATHER THAN HOPED AT. The widest pair is
             * `Complete sprint` + `Edit` on the active sprint: at 12px semibold with `px-2.5` that
             * is roughly 165px of the 366px usable width, leaving ~200px for the chevron, the
             * truncating name and the state badge — which is enough for `APFS v1.0.33 · ACTIVE`.
             * ⭐ `Edit` is the one label kept to a single word for exactly this reason, and it is
             * unambiguous because it sits INSIDE a region named for its sprint: a screen reader
             * announces "Backlog by sprint → APFS v1.0.33 → Edit".
             */}
            {onStart ? (
              <button
                type="button"
                onClick={onStart}
                disabled={starting}
                className="h-9 rounded bg-[color:var(--color-accent)] px-2.5 text-[12px] font-semibold whitespace-nowrap text-[color:var(--color-on-accent)] disabled:opacity-50"
              >
                {starting ? 'Starting…' : 'Start sprint'}
              </button>
            ) : null}

            {onComplete ? (
              <button
                type="button"
                onClick={onComplete}
                disabled={completing}
                className="h-9 rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2.5 text-[12px] font-semibold whitespace-nowrap disabled:opacity-50"
              >
                {completing ? 'Completing…' : 'Complete sprint'}
              </button>
            ) : null}

            {onEdit ? (
              <button
                type="button"
                onClick={onEdit}
                className="h-9 rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2.5 text-[12px] font-semibold whitespace-nowrap"
              >
                Edit
              </button>
            ) : null}
          </>
        ) : undefined
      }
    >
      {loading ? <ListSkeleton rows={4} /> : null}

      {error ? (
        <div className="px-3 py-2 sm:px-4">
          <Notice tone="warning" title="This section could not be loaded">
            <p>{error} — it is not empty; we could not ask.</p>
          </Notice>
        </div>
      ) : null}

      {!loading && !error && rows.length === 0 ? (
        <EmptyState
          title={filtered ? 'Nothing here matches this filter' : 'This section is empty'}
          detail={
            filtered
              ? `Nothing in ${section.name} matched ${filterSummary}. The request succeeded — this is an empty result, not a failure.`
              : section.sprint
                ? 'No tickets are assigned to this sprint.'
                : 'Every ticket in this project belongs to a sprint.'
          }
        />
      ) : null}

      {rows.map((row) => (
        <TicketRow
          key={`${row.kind}-${row.ticket.id}`}
          ticket={row.ticket}
          selected={row.ticket.id === selectedId}
          onSelect={(chosen) => onSelect(chosen.id)}
          onStatusChanged={onStatusChanged}
          /**
           * ⛔ ABSENT ON A NESTED CHILD, which is what keeps `ticket-tree.ts` honest. A `child` row is
           *   drawn under its parent whatever its own rank, so a handle there would offer a move that
           *   changes a number and nothing on screen. `isReorderable` is the single place that rule
           *   lives; the row just does what it is told.
           */
          reorder={
            isReorderable(row)
              ? {
                  dragging: reorder.draggingId === row.ticket.id,
                  dropTarget:
                    reorder.overId === row.ticket.id && reorder.draggingSection === section.key,
                  busy: reorder.busyId !== null,
                  canMoveUp: order.indexOf(row.ticket.id) > 0,
                  canMoveDown: order.indexOf(row.ticket.id) < order.length - 1,
                  onDragStart: () => reorder.begin(section.key, row.ticket.id),
                  onDragEnd: reorder.end,
                  onDragOver: (event) => {
                    // ⚠️ preventDefault is what MAKES a drop target: without it the browser refuses
                    //    the drop and `onDrop` never fires at all.
                    event.preventDefault();
                    reorder.over(section.key, row.ticket.id);
                  },
                  onDragLeave: () => reorder.leave(row.ticket.id),
                  onDrop: (event) => {
                    event.preventDefault();
                    reorder.drop(section.key, row.ticket.id, rows);
                  },
                  onMove: (delta) => reorder.move(section.key, row.ticket.id, delta, rows),
                }
              : undefined
          }
          expansion={
            row.hasChildren
              ? {
                  expanded: row.expanded,
                  childCount: row.childCount,
                  countIsFloor: row.countIsFloor,
                  childrenIncomplete: row.childrenIncomplete,
                  onToggle: () => onToggleExpanded(row.ticket.id),
                }
              : undefined
          }
          nesting={{
            depth: row.depth,
            parentKey: row.parentKey,
            detached: row.kind === 'detached',
            onOpenParent:
              row.kind === 'detached' && row.ticket.parent_id
                ? () => onSelect(row.ticket.parent_id)
                : undefined,
          }}
        />
      ))}

      {/* ⭐ Where the missing rows went — the honesty the paginator owes on the other screen. A
          section's count includes its sub-tasks, and nesting draws some of them under a parent rather
          than in the list, so the two numbers legitimately differ. */}
      {nested > 0 ? (
        <p className="px-3 py-1.5 text-[11.5px] text-[color:var(--color-ink-subtle)] sm:px-4">
          {nested} shown under {nested === 1 ? 'its parent' : 'their parents'}
        </p>
      ) : null}

      {/* ⚠️ Only when the page cap actually bit. The section is now one server-answered request
          (`noSprint=true`), so in the ordinary case it holds everything and there is nothing to
          confess — a permanent "this is complete" line would be noise. ⛔ But 100 is still the cap,
          and a list of 100 sitting under a heading that says 140 must say which it is showing. */}
      {scan?.truncated ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-[color:var(--color-line)] px-3 py-2 text-[11.5px] text-[color:var(--color-ink-muted)] sm:px-4">
          <span className="min-w-0">
            Showing the first {scan.shown} of {scan.total} tickets with no sprint — the API returns at
            most {SECTION_PAGE_SIZE} rows per request, so the points total above is a floor.
          </span>
        </div>
      ) : null}
    </SprintSection>
  );
}

export { UNASSIGNED_SECTION_KEY };
export default SprintBacklogPage;
